import React from "react";
import {
  Drawer,
  Form,
  Input,
  Select,
  DatePicker,
  Divider,
  FormInstance,
} from "antd";
import {
  RawMaterial,
  RawMaterialPurchase,
  RawMaterialPurchaseItem,
  Supplier,
  RawPurchaseAction,
} from "../../../Types";
import { PurchaseStatus } from "../../../Types";
import { useSectionAccess } from "../../../Services";
import { MaterialPicker } from "./MaterialPicker";
import { PurchaseItemsEditor } from "./PurchaseItemsEditor";
import { PurchaseSummaryPanel } from "./PurchaseSummaryPanel";
import { PurchaseInvoiceView } from "./PurchaseInvoiceView";
import {
  drawerFooter,
  drawerTitle as renderDrawerTitle,
} from "../../../components";

const { Option } = Select;

interface Props {
  open: boolean;
  editingPurchase: RawMaterialPurchase | null;
  form: FormInstance;
  suppliers: Supplier[];
  allRawMaterials: RawMaterial[];
  materialSearchText: string;
  purchaseItems: RawMaterialPurchaseItem[];
  globalDiscount: number;
  paidAmount: number;
  grossTotal: number;
  grandTotal: number;
  dueAmount: number;
  isSubmitting: boolean;
  onClose: () => void;
  onSaveDraft: () => void;
  onComplete: () => void;
  onMaterialSearchChange: (v: string) => void;
  onAddMaterial: (id: string) => void;
  onUpdateItem: (
    index: number,
    key: keyof RawMaterialPurchaseItem,
    val: any,
  ) => void;
  onRemoveItem: (index: number) => void;
  onDiscountChange: (v: number) => void;
  onPaidChange: (v: number) => void;
}

export const RawPurchaseFormDrawer: React.FC<Props> = ({
  open,
  editingPurchase,
  form,
  suppliers,
  allRawMaterials,
  materialSearchText,
  purchaseItems,
  globalDiscount,
  paidAmount,
  grossTotal,
  grandTotal,
  dueAmount,
  isSubmitting,
  onClose,
  onSaveDraft,
  onComplete,
  onMaterialSearchChange,
  onAddMaterial,
  onUpdateItem,
  onRemoveItem,
  onDiscountChange,
  onPaidChange,
}) => {
  const { isAllowed } = useSectionAccess("raw_purchases");
  const isCompleted = editingPurchase?.status === PurchaseStatus.COMPLETED;

  const drawerTitle = editingPurchase
    ? isCompleted
      ? "Purchase Invoice"
      : "Edit Purchase Draft"
    : "Record Raw Purchase";

  return (
    <Drawer
      title={renderDrawerTitle(drawerTitle)}
      width={820}
      onClose={onClose}
      open={open}
      styles={{ body: { paddingBottom: isCompleted ? 24 : 80 } }}
      footer={
        !isCompleted
          ? drawerFooter({
              left: (
                <span>
                  Selected Items:{" "}
                  <span className="text-indigo-600 font-semibold">
                    {purchaseItems.length}
                  </span>
                </span>
              ),
              actions: [
                { label: "Cancel", onClick: onClose },
                {
                  label: "Save Draft",
                  loading: isSubmitting,
                  onClick: onSaveDraft,
                },
                ...(isAllowed(RawPurchaseAction.Approve)
                  ? [
                      {
                        label: "Complete Purchase" as const,
                        type: "primary" as const,
                        loading: isSubmitting,
                        onClick: onComplete,
                      },
                    ]
                  : []),
              ],
            })
          : drawerFooter({
              actions: [{ label: "Close", onClick: onClose }],
            })
      }
    >
      {isCompleted && editingPurchase ? (
        <PurchaseInvoiceView purchase={editingPurchase} />
      ) : (
        <Form form={form} layout="vertical" className="flex flex-col gap-1">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Form.Item
              label="Supplier"
              name="supplier_id"
              rules={[{ required: true, message: "Required" }]}
            >
              <Select
                placeholder="Select supplier"
                showSearch
                optionFilterProp="children"
              >
                {suppliers.map((s) => (
                  <Option key={s.id} value={s.id}>
                    {s.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Purchase Date"
              name="purchase_date"
              rules={[{ required: true, message: "Required" }]}
            >
              <DatePicker className="w-full" format="YYYY-MM-DD" />
            </Form.Item>

            <Form.Item label="Invoice No" name="invoice_no">
              <Input placeholder="Optional" />
            </Form.Item>

            <Form.Item label="PO No" name="po_no">
              <Input placeholder="Optional" />
            </Form.Item>
          </div>

          <Divider className="my-2">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
              Items
            </span>
          </Divider>

          <div className="mb-3">
            <MaterialPicker
              allRawMaterials={allRawMaterials}
              searchText={materialSearchText}
              onSearchChange={onMaterialSearchChange}
              onAdd={onAddMaterial}
            />
          </div>

          <div className="mb-4">
            <PurchaseItemsEditor
              items={purchaseItems}
              readOnly={false}
              onUpdate={onUpdateItem}
              onRemove={onRemoveItem}
            />
          </div>

          <Divider className="my-2">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
              Payment Summary
            </span>
          </Divider>

          <PurchaseSummaryPanel
            grossTotal={grossTotal}
            grandTotal={grandTotal}
            dueAmount={dueAmount}
            globalDiscount={globalDiscount}
            paidAmount={paidAmount}
            readOnly={false}
            onDiscountChange={onDiscountChange}
            onPaidChange={onPaidChange}
          />
        </Form>
      )}
    </Drawer>
  );
};
